import { useCallback, useMemo } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { resources } from '../../../core/services/resources';
import type { Document as ApiDocument, DocumentSection, Entity, EntityProperty } from '../../../core/types/data-models';

const MAX_PAGE_SIZE = 2000;

export interface RelationView {
  key: string;
  direction: 'outgoing' | 'incoming';
  relationType: string;
  relationDescription: string | null;
  otherEntityId: number;
  otherEntityName: string;
  otherEntityType: string;
}

export interface MentionView {
  id: number;
  document: ApiDocument | undefined;
  section: DocumentSection | undefined;
  spanText: string | null;
}

/** One project entity of the managed type, with everything EntityKnowledgeCard and EntityDetailPanel show. */
export interface EntityCardData {
  id: number;
  identifier: string;
  name: string;
  description: string | null;
  /** User-curated flag — toggled from the card, see `setManaged`. */
  isManaged: boolean;
  properties: EntityProperty[];
  relations: RelationView[];
  mentions: MentionView[];
}

/**
 * Every project entity of one entity type — name, description, properties,
 * relations (cross-document, deduped like EntityDetailsDialog), and every
 * mention (document — section — the verbatim span the entity was extracted
 * from) — sorted by name.
 */
export function useEntityTypeEntities(
  projectId: number,
  entityType: string,
): {
  entities: EntityCardData[];
  isLoading: boolean;
  setManaged: (entityId: number, isManaged: boolean) => Promise<void>;
} {
  const queryClient = useQueryClient();
  // One row per entity of this type — the cross-section grouping this view used
  // to do client-side is now the shape of the table itself.
  const entitiesQuery = useQuery({
    queryKey: ['managed-knowledge-entities', projectId, entityType],
    queryFn: () =>
      resources.entities.list({
        filter: { projectId: [projectId], entityType: [entityType] },
        sort: 'entityName',
        include: ['properties'],
        pageSize: MAX_PAGE_SIZE,
      }),
    enabled: Number.isFinite(projectId) && !!entityType,
  });
  const rows: Entity[] = entitiesQuery.data?.data ?? [];
  const entityIds = rows.map((r) => r.id);

  /**
   * Flips the user-curated flag and patches the cached row in place, so the
   * checkbox settles without refetching the whole list (and the dependent
   * mention/relation queries, which key on the unchanged entity ids).
   */
  const setManaged = useCallback(
    async (entityId: number, isManaged: boolean) => {
      const updated = await resources.setEntityManaged(entityId, isManaged);
      queryClient.setQueryData<typeof entitiesQuery.data>(
        ['managed-knowledge-entities', projectId, entityType],
        (old) =>
          old && { ...old, data: old.data.map((r) => (r.id === updated.id ? { ...r, isManaged: updated.isManaged } : r)) },
      );
      // The project dashboard lists managed entities first — keep it in step.
      void queryClient.invalidateQueries({ queryKey: ['project-entities'] });
    },
    [queryClient, projectId, entityType],
  );

  const mentionsQuery = useQuery({
    queryKey: ['managed-knowledge-mentions', projectId, entityIds],
    queryFn: () =>
      resources.documentSectionEntities.list({
        filter: { projectId: [projectId], entityId: entityIds },
        pageSize: MAX_PAGE_SIZE,
      }),
    enabled: entityIds.length > 0,
  });
  const mentions = mentionsQuery.data?.data ?? [];

  const documentIds = Array.from(new Set(mentions.map((m) => m.documentId)));
  const documentsQuery = useQuery({
    queryKey: ['managed-knowledge-documents', projectId, documentIds],
    queryFn: () =>
      resources.documents.list({ filter: { projectId: [projectId], id: documentIds }, pageSize: documentIds.length }),
    enabled: documentIds.length > 0,
  });

  const sectionIds = Array.from(new Set(mentions.map((m) => m.documentSectionId).filter((id): id is number => id !== null)));
  const sectionsQuery = useQuery({
    queryKey: ['managed-knowledge-sections', projectId, sectionIds],
    queryFn: () =>
      resources.documentSections.list({ filter: { projectId: [projectId], id: sectionIds }, pageSize: sectionIds.length }),
    enabled: sectionIds.length > 0,
  });

  const outgoingQuery = useQuery({
    queryKey: ['managed-knowledge-relations-out', projectId, entityIds],
    queryFn: () =>
      resources.entityRelations.list({
        filter: { projectId: [projectId], sourceEntityId: entityIds },
        include: ['targetEntity'],
        pageSize: MAX_PAGE_SIZE,
      }),
    enabled: entityIds.length > 0,
  });
  const incomingQuery = useQuery({
    queryKey: ['managed-knowledge-relations-in', projectId, entityIds],
    queryFn: () =>
      resources.entityRelations.list({
        filter: { projectId: [projectId], targetEntityId: entityIds },
        include: ['sourceEntity'],
        pageSize: MAX_PAGE_SIZE,
      }),
    enabled: entityIds.length > 0,
  });

  const entities: EntityCardData[] = useMemo(() => {
    const documentById = new Map((documentsQuery.data?.data ?? []).map((d) => [d.id, d]));
    const sectionById = new Map((sectionsQuery.data?.data ?? []).map((s) => [s.id, s]));

    const mentionsByEntityId = new Map<number, typeof mentions>();
    for (const m of mentions) {
      const list = mentionsByEntityId.get(m.entityId) ?? [];
      list.push(m);
      mentionsByEntityId.set(m.entityId, list);
    }

    // Relations are project-level and unique on (source, target, type), so
    // they group straight onto the entity that owns each endpoint.
    const relationsByEntityId = new Map<number, RelationView[]>();
    const addRelation = (ownerId: number, view: RelationView) => {
      const list = relationsByEntityId.get(ownerId) ?? [];
      list.push(view);
      relationsByEntityId.set(ownerId, list);
    };
    for (const r of outgoingQuery.data?.data ?? []) {
      if (!r.targetEntity) continue;
      addRelation(r.sourceEntityId, {
        key: `out-${r.id}`,
        direction: 'outgoing',
        relationType: r.relationType,
        relationDescription: r.relationDescription,
        otherEntityId: r.targetEntity.id,
        otherEntityName: r.targetEntity.entityName,
        otherEntityType: r.targetEntity.entityType,
      });
    }
    for (const r of incomingQuery.data?.data ?? []) {
      if (!r.sourceEntity) continue;
      addRelation(r.targetEntityId, {
        key: `in-${r.id}`,
        direction: 'incoming',
        relationType: r.relationType,
        relationDescription: r.relationDescription,
        otherEntityId: r.sourceEntity.id,
        otherEntityName: r.sourceEntity.entityName,
        otherEntityType: r.sourceEntity.entityType,
      });
    }

    return rows
      .map((entity) => ({
        id: entity.id,
        identifier: entity.entityIdentifier,
        name: entity.entityName,
        description: entity.entityDescription,
        isManaged: entity.isManaged,
        properties: [...(entity.properties ?? [])].sort((a, b) => a.key.localeCompare(b.key)),
        relations: relationsByEntityId.get(entity.id) ?? [],
        mentions: (mentionsByEntityId.get(entity.id) ?? []).map((m) => ({
          id: m.id,
          document: documentById.get(m.documentId),
          section: m.documentSectionId === null ? undefined : sectionById.get(m.documentSectionId),
          spanText: m.spanText,
        })),
      }))
      .sort((a, b) => a.name.localeCompare(b.name));
  }, [rows, mentions, outgoingQuery.data, incomingQuery.data, documentsQuery.data, sectionsQuery.data]);

  return { entities, isLoading: entitiesQuery.isLoading, setManaged };
}
